import { z } from 'zod/v4';

/**
 * Body of `POST /risk-analysis/assessment-notes`, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/assessment-notes.js` →
 * `create.body`.
 */
export const CreateAssessmentNoteBodySchema = z.object({
  assessmentId: z.string().min(1).max(36),
  riskId: z.string().min(1).max(36),
  content: z.string().min(1),
});

export type CreateAssessmentNoteBody = z.infer<
  typeof CreateAssessmentNoteBodySchema
>;

/**
 * Response of `POST /risk-analysis/assessment-notes`, ported from the
 * `assessmentNote` definition in `routes/schemas/v3/responses.js:911`.
 *
 * The legacy definition also declares `riskTitle`, but the SQL backing this route
 * never selects it, so it's always absent from the response (not even `null`) —
 * omitted here to match what actually serializes.
 */
export const AssessmentNoteResourceSchema = z.object({
  id: z.string().min(1).max(36),
  assessmentId: z.string().min(1).max(36),
  riskId: z.string().min(1).max(36),
  content: z.string().min(1),
  createdByUserId: z.string().min(1).max(36).nullable(),
  createdAt: z.string().min(1).nullable(),
  createdByCompanyId: z.string().min(1).max(36).nullable(),
});

export type AssessmentNoteResource = z.infer<
  typeof AssessmentNoteResourceSchema
>;

/**
 * One row of `GET /risk-analysis/assessments/:id/notes`, ported from
 * `listQuery` in `2025-06/risk-analysis-service/data/assessment-notes/queries/list.js`.
 *
 * Unlike `AssessmentNoteResourceSchema` above, this query's `INNER JOIN` to
 * `risks` really does select `risk_title` — it backs a *different* legacy route
 * (`GET /v3/assessments/:id/notes`, all notes on the assessment) than the one
 * `AssessmentNoteResourceSchema` backs (`GET /v3/assessment-notes`, one risk's
 * notes), which never selects it.
 */
export const AssessmentNoteWithRiskTitleSchema =
  AssessmentNoteResourceSchema.extend({
    riskTitle: z.string().nullable(),
  });

export type AssessmentNoteWithRiskTitle = z.infer<
  typeof AssessmentNoteWithRiskTitleSchema
>;

/**
 * `GET /risk-analysis/assessments/:id/notes` params, ported from
 * `2025-06/risk-analysis-service/routes/http/v3/assessments/list-notes.js`,
 * which reused `assessment.listDetails.params`.
 */
export const GetAssessmentLevelNotesParamsSchema = z.object({
  id: z.string().min(1).max(36),
});

export type GetAssessmentLevelNotesParams = z.infer<
  typeof GetAssessmentLevelNotesParamsSchema
>;

/**
 * Params of `DELETE /risk-analysis/assessment-notes/:id`, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/assessment-notes.js` →
 * `remove.params`.
 */
export const DeleteAssessmentNoteParamsSchema = z.object({
  id: z.string().min(1).max(36),
});

export type DeleteAssessmentNoteParams = z.infer<
  typeof DeleteAssessmentNoteParamsSchema
>;

/** `pagination-config.js` defaults ported verbatim: page 0, count 15, max 500. */
const ASSESSMENT_NOTES_LIST_DEFAULT_PAGE_SIZE = 15;
const ASSESSMENT_NOTES_LIST_MAX_PAGE_SIZE = 500;

/**
 * Querystring of `GET /risk-analysis/assessment-notes`, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/assessment-notes.js` →
 * `list.querystring`.
 */
export const ListAssessmentNotesQuerySchema = z.strictObject({
  assessmentId: z.string().min(1).max(36),
  riskId: z.string().min(1).max(36),
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(ASSESSMENT_NOTES_LIST_MAX_PAGE_SIZE)
    .default(ASSESSMENT_NOTES_LIST_DEFAULT_PAGE_SIZE),
});

export type ListAssessmentNotesQuery = z.infer<
  typeof ListAssessmentNotesQuerySchema
>;

/**
 * `GET /risk-analysis/assessments/:id/notes` querystring, ported from
 * `assessment.listPrioritizationDetails.querystring` (`...paginationParams`) —
 * the only properties that route's schema reused from that shared object.
 * Plain `z.object` (not `strictObject`): the legacy schema declared
 * `additionalProperties: false`, but fastify's global `removeAdditional: true`
 * strips unknown keys before that check runs, so extras were always silently
 * dropped, never rejected with 400 — the pinned integration test locks this in.
 */
export const GetAssessmentLevelNotesQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(ASSESSMENT_NOTES_LIST_MAX_PAGE_SIZE)
    .default(ASSESSMENT_NOTES_LIST_DEFAULT_PAGE_SIZE),
});

export type GetAssessmentLevelNotesQuery = z.infer<
  typeof GetAssessmentLevelNotesQuerySchema
>;
